import React, { useState } from 'react';
import {
  Grid,
  Typography,
  Paper,
  TextField,
  Button,
  MenuItem,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useApiSend } from '../../utils/httpClientTs';
import { createLoan, type CreateLoanRequest } from '../../endpoints/loans';

const LoansTestCreate: React.FC = () => {
  const navigate = useNavigate();
  const [form, setForm] = useState<
    CreateLoanRequest & { loan_number?: string; local_chapter_id?: string }
  >({
    user_id: '',
    amount: 10000,
    interest_rate: 5,
    term_months: 36,
    loan_type: 'standard',
    first_payment_date: new Date().toISOString().slice(0, 10),
    currency: 'USD',
    status: 'active',
    principal_only: 0,
    auto_payment: 0,
    start_date: '',
    anniversary_date: '',
  });
  const [error, setError] = useState<string | null>(null);

  const { mutate: submit, isPending } = useApiSend(
    (payload: CreateLoanRequest) =>
      createLoan({
        ...payload,
        loan_number: payload.loan_number || undefined,
        local_chapter_id: payload.local_chapter_id || undefined,
      }),
    (data) => navigate(`/administration/loans-test/${data.id}`),
    (err: Error) =>
      setError((err as any)?.response?.data?.error ?? err.message),
    [['loans']]
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!form.user_id || !form.first_payment_date || !form.currency) {
      setError('user_id, first_payment_date, and currency are required.');
      return;
    }
    submit({
      ...form,
      loan_number: form.loan_number || undefined,
      local_chapter_id: form.local_chapter_id || undefined,
      start_date: form.start_date || undefined,
      anniversary_date: form.anniversary_date || undefined,
    });
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h5" gutterBottom>
          Create loan (test)
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <Paper sx={{ p: 2 }}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="User ID"
                  value={form.user_id}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, user_id: e.target.value }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 0.01, step: 0.01 }}
                  label="Amount"
                  value={form.amount}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, amount: Number(e.target.value) }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                  label="Interest rate % (e.g. 5 or 2.54)"
                  value={form.interest_rate}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      interest_rate: Number(e.target.value),
                    }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="number"
                  inputProps={{ min: 1 }}
                  label="Term (months)"
                  value={form.term_months}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      term_months: Number(e.target.value),
                    }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Loan type"
                  value={form.loan_type}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, loan_type: e.target.value }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  type="date"
                  label="First payment date"
                  value={form.first_payment_date}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      first_payment_date: e.target.value,
                    }))
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Start date (optional — when loan was provided)"
                  value={form.start_date ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      start_date: e.target.value || undefined,
                    }))
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  type="date"
                  label="Anniversary date (optional — billing anniversary for rollover)"
                  value={form.anniversary_date ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      anniversary_date: e.target.value || undefined,
                    }))
                  }
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  label="Currency"
                  value={form.currency}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, currency: e.target.value }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Status"
                  value={form.status}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, status: e.target.value }))
                  }
                  size="small"
                >
                  <MenuItem value="active">active</MenuItem>
                  <MenuItem value="paid">paid</MenuItem>
                  <MenuItem value="closed">closed</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  select
                  fullWidth
                  label="Principal only (no interest)"
                  value={form.principal_only}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      principal_only: Number(e.target.value),
                    }))
                  }
                  size="small"
                >
                  <MenuItem value={0}>No</MenuItem>
                  <MenuItem value={1}>Yes</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Loan number (optional)"
                  value={form.loan_number ?? ''}
                  onChange={(e) =>
                    setForm((f) => ({
                      ...f,
                      loan_number: e.target.value || undefined,
                    }))
                  }
                  size="small"
                />
              </Grid>
              <Grid item xs={12}>
                {error && (
                  <Typography color="error" sx={{ mb: 1 }}>
                    {error}
                  </Typography>
                )}
                <Button type="submit" variant="contained" disabled={isPending}>
                  {isPending ? 'Creating…' : 'Create loan'}
                </Button>
                <Button
                  sx={{ ml: 1 }}
                  onClick={() => navigate('/administration/loans-test')}
                >
                  Cancel
                </Button>
              </Grid>
            </Grid>
          </form>
        </Paper>
      </Grid>
    </Grid>
  );
};

export default LoansTestCreate;
